// Sidebar.jsx
import { memo, useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import PerfectScrollbar from 'react-perfect-scrollbar';
import MenuList from '../MenuList';
import LogoSection from '../LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';
import useConfig from '../../../hooks/useConfig';
import { drawerWidth } from '../../../store/constant';
import { handlerDrawerOpen, useGetMenuMaster } from '../../../api/menu';

function Sidebar() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { miniDrawer } = useConfig();

  const logo = useMemo(
    () => (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        px: drawerOpen ? 2 : 0,
        py: 1,
        height: '90px',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease-in-out',
        width: '100%',
        mb: 2,
        overflow: 'hidden'
      }}>
        <LogoSection isMini={!drawerOpen} />
      </Box>
    ),
    [drawerOpen]
  );

  const drawerContent = useMemo(() => {
    const content = (
      <Box
        sx={{
          px: drawerOpen ? 0.8 : 0.4,
          mt: downMD ? 0.3 : 0.6,
          pb: 1.0
        }}
      >
        <MenuList />
        {drawerOpen && process.env.REACT_APP_VERSION && (
          <Stack direction="row" justifyContent="center" mt={0.6}>
            <Chip
              label={`v${process.env.REACT_APP_VERSION}`}
              size="small"
              sx={{
                borderRadius: '3px',
                fontSize: '0.6rem',
                height: '16px',
                bgcolor: 'rgba(52, 152, 219, 0.15)',
                color: '#3498DB !important',
                border: '1px solid #3498DB'
              }}
            />
          </Stack>
        )}
      </Box>
    );

    if (downMD) return content;

    return (
      <PerfectScrollbar
        style={{
          height: 'calc(100vh - 74px)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '5px' },
          '&::-webkit-scrollbar-thumb': {
            background: '#BDC3C7',
            borderRadius: '3px'
          }
        }}
      >
        {content}
      </PerfectScrollbar>
    );
  }, [drawerOpen, downMD]);

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { md: 0 },
        width: { xs: 'auto', md: drawerWidth }
      }}
      aria-label="main navigation"
    >
      {downMD || (miniDrawer && drawerOpen) ? (
        <Drawer
          variant={downMD ? 'temporary' : 'persistent'}
          anchor="left"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              mt: downMD ? 0 : 10,
              width: drawerWidth,
              bgcolor: '#F5F7FA',
              color: '#2C3E50',
              borderRight: 'none',
              zIndex: 1099
            }
          }}
        >
          {downMD && logo}
          {drawerContent}
        </Drawer>
      ) : (
        <MiniDrawerStyled variant="permanent" open={drawerOpen}>
          {logo}
          {drawerContent}
        </MiniDrawerStyled>
      )}
    </Box>
  );
}

export default memo(Sidebar);